import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch } from '../api/client';

/** A field the teacher defined for a subject (e.g. Rekenen: Blok as a choice, Les as a number). */
export type FieldKind = 'choice' | 'number' | 'text';
export type SubjectField = {
  id: string;
  label: string;
  kind: FieldKind;
  options: string[];
  min: number;
  max: number;
  /** The last value saved for this field, the basis of the suggestion for the next lesson. */
  lastValue: string;
};
/** What is sent when the fields of a subject are saved; a field without id is new. */
export type FieldInput = { id?: string; label: string; kind: FieldKind; options?: string[]; min?: number; max?: number };
export type Subject = { id: string; name: string; color: string; icon: string; sortOrder: number; fields: SubjectField[] };
export type SubjectPackage = {
  key: string;
  name: string;
  description: string;
  subjects: { name: string; icon: string; color: string; fields: string[] }[];
};
export type ImportResult = { added: number; updated: number; skipped: number; subjects: Subject[] };
export type Period = {
  id: string;
  label: string;
  startTime: string;
  endTime: string;
  sortOrder: number;
  isBreak: boolean;
};
export type Lesson = {
  periodId: string;
  weekday: number;
  subjectId: string | null;
  note: string;
  /** Day plan, only on lessons of a real week. */
  goal?: string;
  materials?: string;
  /** Values of the subject's own fields, by field id. */
  fieldValues?: Record<string, string>;
};
/** The free note under one day of a real week. */
export type DayNote = { weekday: number; note: string };
export type DefaultWeek = { periods: Period[]; lessons: Lesson[]; dayNotes?: DayNote[] };
export type CellValue = { subjectId: string | null; note: string };
export type SubjectInput = { name: string; color: string; icon: string };
export type PeriodInput = { label: string; startTime: string; endTime: string; isBreak: boolean };

const jsonBody = (body: unknown): RequestInit => ({
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

async function getJson<T>(path: string): Promise<T> {
  return (await (await apiFetch(path)).json()) as T;
}

export const subjectsKey = ['subjects'] as const;
export const defaultWeekKey = ['default-week'] as const;

export function useSubjects() {
  return useQuery({ queryKey: subjectsKey, queryFn: () => getJson<Subject[]>('/api/subjects') });
}

export function useDefaultWeek() {
  return useQuery({ queryKey: defaultWeekKey, queryFn: () => getJson<DefaultWeek>('/api/default-week') });
}

export function useSubjectMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: subjectsKey });
  return {
    create: useMutation({
      mutationFn: (input: SubjectInput) => apiFetch('/api/subjects', { method: 'POST', ...jsonBody(input) }),
      onSuccess: refresh,
    }),
    update: useMutation({
      mutationFn: ({ id, ...input }: SubjectInput & { id: string }) =>
        apiFetch(`/api/subjects/${id}`, { method: 'PATCH', ...jsonBody(input) }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (id: string) => apiFetch(`/api/subjects/${id}`, { method: 'DELETE' }),
      // Lessons keep their note but lose the subject, so the week must reload as well.
      onSuccess: () =>
        Promise.all([refresh(), queryClient.invalidateQueries({ queryKey: defaultWeekKey })]),
    }),
    reorder: useMutation({
      mutationFn: (ids: string[]) => apiFetch('/api/subjects/order', { method: 'PUT', ...jsonBody(ids) }),
      onSuccess: refresh,
    }),
  };
}

/** Replaces the fields of one subject; the subjects reload with the saved fields (and their ids). */
export function useSubjectFieldsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fields }: { id: string; fields: FieldInput[] }) =>
      apiFetch(`/api/subjects/${id}/fields`, { method: 'PUT', ...jsonBody(fields) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: subjectsKey }),
  });
}

export function useSubjectPackages() {
  return useQuery({ queryKey: ['subject-packages'], queryFn: () => getJson<SubjectPackage[]>('/api/subject-packages') });
}

/** Imports a ready-made subject list; existing subjects (same name) are left as they are. */
export function useImportSubjectPackage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (key: string) =>
      (await (await apiFetch(`/api/subject-packages/${key}/import`, { method: 'POST' })).json()) as ImportResult,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: subjectsKey }),
  });
}

export function usePeriodMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: defaultWeekKey });
  return {
    create: useMutation({
      mutationFn: (input: PeriodInput) => apiFetch('/api/default-periods', { method: 'POST', ...jsonBody(input) }),
      onSuccess: refresh,
    }),
    update: useMutation({
      mutationFn: ({ id, ...input }: PeriodInput & { id: string }) =>
        apiFetch(`/api/default-periods/${id}`, { method: 'PATCH', ...jsonBody(input) }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (id: string) => apiFetch(`/api/default-periods/${id}`, { method: 'DELETE' }),
      onSuccess: refresh,
    }),
  };
}

type CellChange = CellValue & { periodId: string; weekday: number };

/** Fills or clears one cell. The cache is updated at once (autosave feels instant) and rolled back on failure. */
function useCellMutation(queryKey: readonly unknown[], lessonsPath: string, mutationKey?: readonly unknown[]) {
  const queryClient = useQueryClient();
  return useMutation({
    ...(mutationKey ? { mutationKey } : {}),
    mutationFn: ({ periodId, weekday, subjectId, note }: CellChange) =>
      apiFetch(`${lessonsPath}/${periodId}/${weekday}`, { method: 'PUT', ...jsonBody({ subjectId, note }) }),
    onMutate: async (change) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<DefaultWeek>(queryKey);
      if (previous) {
        const existing = previous.lessons.find(
          (lesson) => lesson.periodId === change.periodId && lesson.weekday === change.weekday,
        );
        const others = previous.lessons.filter(
          (lesson) => !(lesson.periodId === change.periodId && lesson.weekday === change.weekday),
        );
        const cleared = change.subjectId === null && change.note.trim() === '';
        queryClient.setQueryData<DefaultWeek>(queryKey, {
          ...previous,
          lessons: cleared
            ? others
            : [
                ...others,
                {
                  ...existing,
                  periodId: change.periodId,
                  weekday: change.weekday,
                  subjectId: change.subjectId,
                  note: change.note.trim(),
                  // Another subject has other fields, so its predecessor's values are gone.
                  ...(existing?.subjectId === change.subjectId ? {} : { fieldValues: {} }),
                },
              ],
        });
      }
      return { previous };
    },
    onError: (_error, _change, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
}

export function useDefaultLessonMutation() {
  return useCellMutation(defaultWeekKey, '/api/default-lessons');
}

// ---- real weeks ------------------------------------------------------------------------------

export type WeekSource = 'default' | 'previous';

export const weekKey = (year: number, week: number) => ['week', year, week] as const;
/** Every write to a week carries this key, so the toolbar can show what the autosave is doing. */
export const weekSaveKey = (year: number, week: number) => ['week-save', year, week] as const;

/** The week with its periods and lessons, or null when it has not been created yet (404). */
export function useWeek(year: number, week: number) {
  return useQuery({
    queryKey: weekKey(year, week),
    queryFn: async (): Promise<DefaultWeek | null> => {
      try {
        return await getJson<DefaultWeek>(`/api/weeks/${year}/${week}`);
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
  });
}

export function useCreateWeek(year: number, week: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (from: WeekSource) => apiFetch(`/api/weeks/${year}/${week}`, { method: 'POST', ...jsonBody({ from }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: weekKey(year, week) }),
  });
}

/** A week without periods takes over the standard periods and lessons. */
export function useFillWeekFromDefault(year: number, week: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch(`/api/weeks/${year}/${week}/defaults`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: weekKey(year, week) }),
  });
}

export function useWeekLessonMutation(year: number, week: number) {
  return useCellMutation(weekKey(year, week), `/api/weeks/${year}/${week}/lessons`, weekSaveKey(year, week));
}

/** Saves the day-plan fields of one lesson; the week reloads so the cell and the day page stay in step. */
export function useWeekDetailsMutation(year: number, week: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: weekSaveKey(year, week),
    mutationFn: ({ periodId, weekday, ...details }: { periodId: string; weekday: number; goal: string; materials: string }) =>
      apiFetch(`/api/weeks/${year}/${week}/lessons/${periodId}/${weekday}/details`, { method: 'PUT', ...jsonBody(details) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: weekKey(year, week) }),
  });
}

/** Sets values of the subject's own fields on a lesson (an empty value clears the field). */
export function useWeekFieldMutation(year: number, week: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: weekSaveKey(year, week),
    mutationFn: ({ periodId, weekday, values }: { periodId: string; weekday: number; values: Record<string, string> }) =>
      apiFetch(`/api/weeks/${year}/${week}/lessons/${periodId}/${weekday}/fields`, { method: 'PUT', ...jsonBody(values) }),
    // Reload both: the week shows the values, the subjects carry the last value used for the next suggestion.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: weekKey(year, week) }),
        queryClient.invalidateQueries({ queryKey: subjectsKey }),
      ]),
  });
}

export function useWeekPeriodMutations(year: number, week: number) {
  const queryClient = useQueryClient();
  const base = `/api/weeks/${year}/${week}/periods`;
  const refresh = () => queryClient.invalidateQueries({ queryKey: weekKey(year, week) });
  const mutationKey = weekSaveKey(year, week);
  return {
    create: useMutation({
      mutationKey,
      mutationFn: (input: PeriodInput) => apiFetch(base, { method: 'POST', ...jsonBody(input) }),
      onSuccess: refresh,
    }),
    update: useMutation({
      mutationKey,
      mutationFn: ({ id, ...input }: PeriodInput & { id: string }) =>
        apiFetch(`${base}/${id}`, { method: 'PATCH', ...jsonBody(input) }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationKey,
      mutationFn: (id: string) => apiFetch(`${base}/${id}`, { method: 'DELETE' }),
      onSuccess: refresh,
    }),
  };
}

/** The free note under one day of the week; an empty note removes it. */
export function useWeekDayNoteMutation(year: number, week: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: weekSaveKey(year, week),
    mutationFn: ({ weekday, note }: DayNote) =>
      apiFetch(`/api/weeks/${year}/${week}/notes/${weekday}`, { method: 'PUT', ...jsonBody({ note }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: weekKey(year, week) }),
  });
}
